/**
 * Universal analytics layer — GA4 + Meta Pixel + Google Tag Manager.
 *
 * Activates per-channel only when the corresponding env var is set, so the
 * code ships safely even before IDs are configured. Add to /app/frontend/.env:
 *
 *   REACT_APP_GA4_ID=G-XXXXXXXXXX
 *   REACT_APP_META_PIXEL_ID=000000000000000
 *   REACT_APP_GTM_ID=GTM-XXXXXXX
 *
 * Usage:
 *   1) <Analytics /> mounted once in App.js (auto-tracks SPA route changes)
 *   2) import { trackEvent } from "@/lib/analytics" — fire custom conversion events
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA4_ID    = process.env.REACT_APP_GA4_ID;
const PIXEL_ID  = process.env.REACT_APP_META_PIXEL_ID;
const GTM_ID    = process.env.REACT_APP_GTM_ID;

let booted = false;
function bootScripts() {
  if (booted || typeof window === "undefined") return;
  booted = true;

  // ── Google Tag Manager (umbrella container — handles GA4 / Ads / others)
  if (GTM_ID) {
    const s = document.createElement("script");
    s.async = true;
    s.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s);j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i;
      f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`;
    document.head.appendChild(s);
  }

  // ── Google Analytics 4 (direct, in case user runs without GTM)
  if (GA4_ID && !GTM_ID) {
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.innerHTML = `window.dataLayer=window.dataLayer||[];
      function gtag(){dataLayer.push(arguments);}
      window.gtag=gtag;gtag('js',new Date());gtag('config','${GA4_ID}',{anonymize_ip:true});`;
    document.head.appendChild(s2);
  } else if (GA4_ID) {
    // GTM container will load GA4 — just initialise dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  }

  // ── Meta (Facebook) Pixel
  if (PIXEL_ID) {
    const s = document.createElement("script");
    s.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){
      n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
      t=b.createElement(e);t.async=!0;t.src=v;
      s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
      (window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init','${PIXEL_ID}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
}

/** Fire a custom conversion event across all configured platforms. */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  // GA4 / GTM
  if (window.gtag) {
    try { window.gtag("event", name, params); } catch { /* noop */ }
  } else if (window.dataLayer) {
    try { window.dataLayer.push({ event: name, ...params }); } catch { /* noop */ }
  }
  // Meta Pixel — only emit standard events to "track", everything else to "trackCustom"
  if (window.fbq) {
    const STANDARD = new Set(["Lead", "Contact", "CompleteRegistration", "Schedule",
                              "Subscribe", "ViewContent", "Search"]);
    try {
      if (STANDARD.has(name)) window.fbq("track", name, params);
      else window.fbq("trackCustom", name, params);
    } catch { /* noop */ }
  }
}

/** React component — mount once in root. Auto-tracks SPA route changes. */
export default function Analytics() {
  const { pathname, search } = useLocation();
  useEffect(() => { bootScripts(); }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const page_path = pathname + (search || "");
    if (window.gtag && GA4_ID) {
      window.gtag("event", "page_view", { page_path, page_title: document.title });
    }
    if (window.dataLayer && GTM_ID) {
      window.dataLayer.push({ event: "page_view", page_path });
    }
    if (window.fbq && PIXEL_ID) window.fbq("track", "PageView");
  }, [pathname, search]);
  return null;
}
